import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { useAuthStore } from '@/lib/auth-store'
import { Logo } from '@/components/Logo'
import { cn } from '@/lib/utils'
import type { SessionUser } from '@/lib/session-types'

interface PlanOption {
  plan: 'solo' | 'management' | 'enterprise'
  monthlyMinor: number
  currency: string
  features: string[]
}

interface RegisterResponse {
  accessToken: string
  refreshToken: string
  user: SessionUser
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

export function JoinArtistPage() {
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [organisationName, setOrganisationName] = useState('')
  const [artistName, setArtistName] = useState('')
  const [plan, setPlan] = useState<PlanOption['plan']>('solo')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const { data: plans } = useQuery<PlanOption[], ApiClientError>({
    queryKey: ['onboarding-plans'],
    queryFn: () => apiFetch<PlanOption[]>('/auth/onboarding/plans', { skipAuth: true }),
  })

  const orgSlug = slugify(organisationName)
  const artistSlug = slugify(artistName)

  // Auto-fill artist name from org name on first edit
  useEffect(() => {
    if (artistName === '' && organisationName) {
      setArtistName(organisationName)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationName])

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const data = await apiFetch<RegisterResponse>('/auth/register/artist', {
        method: 'POST',
        body: {
          email,
          password,
          displayName,
          organisationName,
          organisationSlug: orgSlug,
          artistName,
          artistSlug,
          plan,
        },
        skipAuth: true,
      })
      setSession(data)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-gradient-to-b from-ovation-paper to-white px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        <form
          onSubmit={onSubmit}
          className="space-y-6 rounded-xl border border-ovation-ink/10 bg-white p-8 shadow-xl shadow-ovation-ink/5"
        >
          <div>
            <h1 className="font-display text-3xl tracking-tight">Sign up as an artist</h1>
            <p className="mt-1 text-sm text-ovation-muted">
              Management companies, artists, and labels. Includes the full Ovation
              back office plus your first artist tenant.
            </p>
          </div>

          <section className="space-y-4">
            <SectionLabel>Account</SectionLabel>
            <Field id="displayName" label="Your name" value={displayName} onChange={setDisplayName} required autoComplete="name" />
            <Field id="email" label="Email" type="email" value={email} onChange={setEmail} required autoComplete="email" />
            <Field id="password" label="Password" type="password" value={password} onChange={setPassword} required minLength={8} autoComplete="new-password" hint="At least 8 characters." />
          </section>

          <section className="space-y-4">
            <SectionLabel>Organisation</SectionLabel>
            <Field
              id="organisationName"
              label="Management / company name"
              value={organisationName}
              onChange={setOrganisationName}
              required
              hint={orgSlug ? `URL slug: ${orgSlug}` : undefined}
            />
            <Field
              id="artistName"
              label="First artist on the roster"
              value={artistName}
              onChange={setArtistName}
              required
              hint={artistSlug ? `URL slug: ${artistSlug}` : undefined}
            />
          </section>

          <section className="space-y-3">
            <SectionLabel>Plan</SectionLabel>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {plans?.map((p) => (
                <button
                  key={p.plan}
                  type="button"
                  onClick={() => setPlan(p.plan)}
                  className={cn(
                    'rounded-lg border p-4 text-left transition-colors',
                    plan === p.plan
                      ? 'border-ovation-ink bg-ovation-ink/5'
                      : 'border-ovation-ink/15 hover:border-ovation-ink/30',
                  )}
                >
                  <div className="font-display text-lg capitalize">{p.plan}</div>
                  <div className="mt-1 text-sm">
                    £{(p.monthlyMinor / 100).toLocaleString()}
                    <span className="text-xs text-ovation-muted">/mo per artist</span>
                  </div>
                  <ul className="mt-3 space-y-1 text-xs text-ovation-muted">
                    {p.features.slice(0, 4).map((f) => (
                      <li key={f}>• {f}</li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
            <p className="text-xs text-ovation-muted">
              Card capture happens after sign-up via Stripe Checkout (currently a placeholder
              in dev — no charge).
            </p>
          </section>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ovation-ink px-4 py-3 text-sm font-medium text-ovation-paper transition-colors hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {submitting ? 'Creating account…' : 'Create artist account'}
          </button>

          <div className="text-center text-xs text-ovation-muted">
            <Link to="/join" className="hover:text-ovation-ink">
              ← Free crew account instead
            </Link>
            {' · '}
            <Link to="/login" className="hover:text-ovation-ink">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ovation-muted">
      {children}
    </div>
  )
}

interface FieldProps {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  type?: string | undefined
  autoComplete?: string | undefined
  required?: boolean | undefined
  minLength?: number | undefined
  hint?: string | undefined
}

function Field(props: FieldProps) {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-sm font-medium">
        {props.label}
      </label>
      <input
        id={props.id}
        type={props.type ?? 'text'}
        autoComplete={props.autoComplete}
        required={props.required}
        minLength={props.minLength}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
      />
      {props.hint && <p className="mt-1 text-xs text-ovation-muted">{props.hint}</p>}
    </div>
  )
}
